import { Link } from "react-router-dom";
import { ArrowRight, TrendingUp, DollarSign, Radar, Users, Music4, BarChart3, Zap, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import Reveal from "@/components/site/Reveal";
import SectionHeading from "@/components/site/SectionHeading";

const BENEFITS = [
  { icon: TrendingUp,  tone: "am-tile-mint",    title: "Audience growth",     copy: "Reach listeners who choose to engage — because they earn while they do." },
  { icon: DollarSign,  tone: "am-tile-pink",    title: "New revenue streams", copy: "RPM tokens flow through your catalog. Attention becomes monetization." },
  { icon: Radar,       tone: "am-tile-lavender",title: "Real visibility",     copy: "LiveLink surfaces your music anywhere it plays — radio, TV, in the wild." },
  { icon: Users,       tone: "am-tile-peach",   title: "True fandom",         copy: "Fans with skin in the game share, follow, and champion your work." },
  { icon: BarChart3,   tone: "am-tile-sky",     title: "Deep analytics",      copy: "See who's mining your tracks, how long, and where the moments hit hardest." },
  { icon: Zap,         tone: "am-tile-lemon",   title: "Instant activation",  copy: "Upload, embed RPM, publish. Your existing catalog is already ready." },
];

export default function ForArtists() {
  return (
    <main data-testid="page-for-artists" className="pt-24 md:pt-32">
      {/* HERO */}
      <section className="relative">
        <div className="container relative">
          <div className="am-blob am-blob-orange" style={{ right: "-140px", top: "-40px" }} />

          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 flex flex-col gap-8">
              <Reveal>
                <span className="inline-flex items-center gap-2 rounded-full bg-white/80 backdrop-blur px-4 py-1.5 text-[11px] uppercase tracking-[0.24em] text-primary font-semibold border border-black/5 self-start">
                  <Music4 className="h-3.5 w-3.5" /> For Artists
                </span>
              </Reveal>
              <Reveal delay={80}>
                <h1 className="font-display font-bold tracking-tight text-[clamp(2.6rem,6.5vw,5.75rem)] leading-[0.98]">
                  Mine your <span className="am-sunset-text">music.</span>
                  <br /><span className="font-serif-editorial italic font-normal">Own the moment</span> they hit play.
                </h1>
              </Reveal>
              <Reveal delay={160}>
                <p className="text-lg md:text-xl text-foreground/65 max-w-xl leading-relaxed">
                  AudioMine gives independent artists a direct line to the audience — a platform where attention is measured, rewarded, and turned into a growing community around your sound.
                </p>
              </Reveal>
              <Reveal delay={220}>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/contact?type=artist">
                    <Button data-testid="artists-cta-apply" size="lg" className="rounded-full h-14 px-8 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold text-base shadow-[0_10px_30px_-8px_rgba(250,37,104,0.55)]">
                      Apply as an Artist <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </Link>
                  <Link to="/how-it-works">
                    <Button size="lg" variant="outline" className="rounded-full h-14 px-8 border-black/10 bg-white hover:bg-white/70 text-foreground font-semibold text-base">
                      See how it works
                    </Button>
                  </Link>
                </div>
              </Reveal>
            </div>

            <div className="lg:col-span-5">
              <Reveal delay={220}>
                <div className="relative">
                  <div className="am-soft aspect-[4/5] overflow-hidden rounded-[40px]">
                    <img src="https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?auto=format&fit=crop&w=1000&q=80" alt="" className="h-full w-full object-cover" />
                  </div>
                  <div className="absolute -bottom-6 -left-6 md:-left-10 am-soft rounded-3xl p-5 max-w-xs bg-white flex items-center gap-4">
                    <div className="h-12 w-12 rounded-2xl am-tile-pink flex items-center justify-center">
                      <TrendingUp className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <div className="font-display font-bold">+312%</div>
                      <div className="text-xs text-foreground/55">Deep-listen engagement</div>
                    </div>
                  </div>
                  <div className="absolute -top-4 -right-4 md:-right-8 am-soft rounded-2xl px-5 py-3 bg-white text-sm font-medium flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-primary" /> RPM live
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-24 md:py-32 relative">
        <div className="container">
          <SectionHeading
            eyebrow="What you unlock"
            title="Six ways AudioMine changes the game."
            subtitle="Built to reward the creators the algorithms usually forget."
          />
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {BENEFITS.map((b, i) => {
              const Icon = b.icon;
              return (
                <Reveal key={b.title} delay={i * 80}>
                  <div className="am-soft p-8 h-full flex flex-col gap-5">
                    <div className={`h-14 w-14 rounded-2xl ${b.tone} flex items-center justify-center text-foreground`}>
                      <Icon className="h-6 w-6" />
                    </div>
                    <div className="font-display font-bold text-xl">{b.title}</div>
                    <p className="text-foreground/60 leading-relaxed">{b.copy}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROMISE STRIP */}
      <section className="py-16 md:py-24 bg-secondary/50">
        <div className="container grid md:grid-cols-3 gap-8 text-center">
          {[
            ["No middlemen", "Direct listener-to-artist reward loop."],
            ["Full catalog support", "Enable RPM on any track, any time."],
            ["Analytics that matter", "Attention, region, gamification depth."],
          ].map(([t, d]) => (
            <Reveal key={t}>
              <div className="flex flex-col gap-2">
                <div className="font-display font-bold text-3xl md:text-4xl am-sunset-text">{t}</div>
                <div className="text-sm text-foreground/60">{d}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32">
        <div className="container">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <SectionHeading
              eyebrow="Ready to move?"
              title="Get on the artist list."
              subtitle="Tell us about your project — we'll walk you through onboarding, RPM embedding, and how to start rewarding your fans this quarter."
            />
            <Reveal delay={200}>
              <div className="am-soft rounded-[32px] p-8 md:p-10 flex flex-col gap-5 bg-white">
                <p className="text-foreground/70">Two-minute application. No spam. A real human replies.</p>
                <Link to="/contact?type=artist">
                  <Button data-testid="artists-cta-bottom" size="lg" className="w-full rounded-full h-14 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold text-base">
                    Apply as an Artist <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}
